import { useState, useEffect, useRef } from 'react';
import type { IAccessProperties } from '../../model/QuickSaveModel';
import {
    getAccessCodes,
    getLawCodeTableResults,
    getAccessGroups,
    type AccessCodeResult,
    type LawCodeResult,
    type AccessGroupResult
} from '../../../../services/api/dataServiceAccessReview';

interface UseAccessRightsDataProps {
    documentArchive?: number;
    initialAccessProperties?: IAccessProperties;
}

export const useAccessRightsData = ({ documentArchive, initialAccessProperties }: UseAccessRightsDataProps) => {
    // Dropdown data
    const [accessCodes, setAccessCodes] = useState<AccessCodeResult[]>([]);
    const [lawCodes, setLawCodes] = useState<LawCodeResult[]>([]);
    const [accessGroups, setAccessGroups] = useState<AccessGroupResult[]>([]);

    // Selected values
    const [selectedAccessCode, setSelectedAccessCode] = useState<string>(
        initialAccessProperties?.AccessCode.toString() ?? ''
    );
    const [selectedParagraph, setSelectedParagraph] = useState<string>(initialAccessProperties?.Paragraph ?? '');
    const [selectedAccessGroup, setSelectedAccessGroup] = useState<string>(
        initialAccessProperties?.AccessGroup.toString() ?? ''
    );
    const previousAccessCode = useRef(selectedAccessCode);

    // Loading states
    const [isLoadingAccessCodes, setIsLoadingAccessCodes] = useState(false);
    const [isLoadingLawCodes, setIsLoadingLawCodes] = useState(false);
    const [isLoadingAccessGroups, setIsLoadingAccessGroups] = useState(false);

    const [error, setError] = useState<string>('');

    // Load access codes when document archive is available
    useEffect(() => {
        if (documentArchive) {
            void loadAccessCodes(documentArchive);
        }
    }, [documentArchive]);

    // Load law codes and access groups when access code changes
    useEffect(() => {
        if (previousAccessCode.current !== selectedAccessCode) {
            setSelectedParagraph('');
            setSelectedAccessGroup('');
        }
        previousAccessCode.current = selectedAccessCode;
        setLawCodes([]);
        setAccessGroups([]);

        // Load new data only if access code is selected
        if (selectedAccessCode && documentArchive) {
            void loadLawCodes(Number.parseInt(selectedAccessCode));
            void loadAccessGroups(documentArchive, Number.parseInt(selectedAccessCode));
        }
    }, [selectedAccessCode, documentArchive]);

    const loadAccessCodes = async (archive: number) => {
        setIsLoadingAccessCodes(true);
        setError('');
        try {
            const codes = await getAccessCodes(archive);
            setAccessCodes(codes);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load access codes';
            setError(errorMessage);
        } finally {
            setIsLoadingAccessCodes(false);
        }
    };

    const loadLawCodes = async (accessCode: number) => {
        setIsLoadingLawCodes(true);
        setError('');
        try {
            const codes = await getLawCodeTableResults(accessCode);
            setLawCodes(codes);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load law code table results';
            setError(errorMessage);
        } finally {
            setIsLoadingLawCodes(false);
        }
    };

    const loadAccessGroups = async (archive: number, accessCode: number) => {
        setIsLoadingAccessGroups(true);
        setError('');
        try {
            const groups = await getAccessGroups(archive, accessCode);
            setAccessGroups(groups);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load access groups';
            setError(errorMessage);
        } finally {
            setIsLoadingAccessGroups(false);
        }
    };

    return {
        // Data
        accessCodes,
        lawCodes,
        accessGroups,

        // Selected values
        selectedAccessCode,
        setSelectedAccessCode,
        selectedParagraph,
        setSelectedParagraph,
        selectedAccessGroup,
        setSelectedAccessGroup,

        // Loading states
        isLoadingAccessCodes,
        isLoadingLawCodes,
        isLoadingAccessGroups,

        // Error
        error,
        setError,
    };
};
